/**
 * displayName — shared helper for rendering an address's user-facing label.
 *
 * Special-cases on-chain "magic" addresses (zero / dEaD) so they read as
 * "Burn/Mint" instead of whatever the entity DB happens to return for them
 * (some sources label the zero address "Null", some "Null: 0x000…000",
 * etc. — confusing to officers reading transaction lists). Pass it the
 * raw address PLUS whatever entity name the backend returned; it picks
 * the meaningful label.
 *
 * Returns:
 *   - "Burn/Mint" for the zero address
 *   - "Burn (dEaD)" for Vitalik's burn address
 *   - the trimmed entityName when it has a meaningful value
 *   - empty string when there's no name to display (caller falls back to
 *     a truncated address)
 */
const ZERO_ADDRESS_LOWER = '0x0000000000000000000000000000000000000000';
const DEAD_ADDRESS_LOWER = '0x000000000000000000000000000000000000dead';

const PLACEHOLDER_NAMES = new Set([
    'unknown',
    'null',
    'null address',
    'null: 0x000…000',
    'null: 0x000...000',
]);

export function displayEntityName(address: string | null | undefined, entityName: string | null | undefined): string {
    const addrLower = (address || '').toLowerCase();
    if (addrLower === ZERO_ADDRESS_LOWER) return 'Burn/Mint';
    if (addrLower === DEAD_ADDRESS_LOWER) return 'Burn (dEaD)';
    const name = (entityName || '').trim();
    if (!name) return '';
    if (PLACEHOLDER_NAMES.has(name.toLowerCase())) return '';
    return name;
}

export function isMagicAddress(address: string | null | undefined): boolean {
    const addrLower = (address || '').toLowerCase();
    return addrLower === ZERO_ADDRESS_LOWER || addrLower === DEAD_ADDRESS_LOWER;
}

/**
 * Prefer an explicit canvas label when it carries more identity than the
 * default two-line renderer (`entity_name` + chain/address subline).
 *
 * Live expansion stamps `label` via formatLabel as either the bare entity
 * name or a truncated address — those fall through so officers still get
 * the address subline. Teaching demos stamp richer labels
 * ("Binance 14\\nHot Wallet"); those MUST win or walkthroughs can't name
 * the node on the map.
 *
 * `user_label` (officer-authored) always wins when set.
 */
export function resolveStoredCanvasLabel(opts: {
    userLabel?: unknown;
    label?: unknown;
    entityName?: unknown;
    address?: string | null;
}): string | null {
    if (typeof opts.userLabel === 'string' && opts.userLabel.trim()) {
        return capCanvasLabel(opts.userLabel.trim());
    }
    if (typeof opts.label !== 'string') return null;
    const trimmed = opts.label.trim();
    if (!trimmed) return null;

    const lines = trimmed
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length === 0) return null;

    const primary = lines[0];
    const entityName =
        typeof opts.entityName === 'string' ? opts.entityName.trim() : '';
    const addr = opts.address || '';

    // formatLabel mirrors — fall through to entity_name + address subline.
    if (
        entityName &&
        primary.toLowerCase() === entityName.toLowerCase() &&
        lines.length === 1
    ) {
        return null;
    }
    if (addr.length >= 10) {
        const trunc6Dots = `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
        const trunc6Ellipsis = `${addr.substring(0, 6)}…${addr.substring(addr.length - 4)}`;
        const trunc8Ellipsis = `${addr.substring(0, 8)}…${addr.substring(addr.length - 4)}`;
        if (
            primary === addr ||
            primary === trunc6Dots ||
            primary === trunc6Ellipsis ||
            primary === trunc8Ellipsis
        ) {
            return null;
        }
    }

    return capCanvasLabel(lines.slice(0, 2).join('\n'));
}

/** Soft per-line cap so multi-line teaching labels still fit the 120×40 card. */
function capCanvasLabel(label: string): string {
    return label
        .split(/\r?\n/)
        .slice(0, 2)
        .map((line, i) => {
            const max = i === 0 ? 18 : 20;
            const t = line.trim();
            return t.length > max ? `${t.slice(0, max - 1)}…` : t;
        })
        .filter(Boolean)
        .join('\n');
}
